import { useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { Card, IconButton } from '@openedx/paragon';
import { RiCheckLine } from '@remixicon/react';
import { useIntl } from '@edx/frontend-platform/i18n';

import messages from './messages';

const COURSE_LABEL_MAX_CHARS = 50;
// A flipped card's back can carry a title, a paragraph and a footnote, so it gets
// longer on screen than a quiz verdict — and unlike a quiz there is no second
// chance, since FSRS pushes `due` out once the review lands.
const BACK_HOLD_MS = 6000;
// Back-less cards have nothing to read after the press; the hold is just the
// beat between "marked" and "gone".
const FRONT_HOLD_MS = 3000;

const truncateLabel = (value, limit) => {
  if (!value || value.length <= limit) { return value; }
  return `${value.slice(0, limit - 1).trimEnd()}…`;
};

const TextFlashcard = ({
  text, courseLabel, quoteTone, back, onAttempt, isDismissing,
}) => {
  const intl = useIntl();
  const isMutedQuote = quoteTone === 'muted';
  const hasBack = !!(back && (back.title || back.body || back.footnote));
  const [isFlipped, setIsFlipped] = useState(false);
  const attemptedRef = useRef(false);

  const displayCourseLabel = truncateLabel(courseLabel, COURSE_LABEL_MAX_CHARS);

  // Text cards have no recall signal, so revealing the back self-reports
  // "got it" → Good (3). Fires once; flipping back and forth after that is free.
  const attempt = (holdMs) => {
    if (attemptedRef.current) { return; }
    attemptedRef.current = true;
    onAttempt?.(3, holdMs);
  };

  const toggleFlip = () => {
    if (isDismissing || !hasBack) { return; }
    setIsFlipped((prev) => !prev);
    attempt(BACK_HOLD_MS);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleFlip();
    }
  };

  // Only reachable on a back-less card: with nothing to flip there is no other
  // way to signal the attempt, so this one keeps its check button.
  const markDone = (event) => {
    event?.stopPropagation?.();
    attempt(FRONT_HOLD_MS);
  };

  const dismissButton = (handler) => (
    <div className="flashcard-item__action">
      <IconButton
        alt={intl.formatMessage(messages.dismissFlashcard)}
        src={RiCheckLine}
        variant="ghost-secondary"
        size="sm"
        onClick={handler}
        disabled={isDismissing}
      />
    </div>
  );

  const frontFace = (
    <div className="flashcard-item__face flashcard-item__face--front">
      <div className="flashcard-item__frame flashcard-item__frame--quote">
        <div className="flashcard-item__quoteBlock">
          <span className="flashcard-item__quoteMark" aria-hidden="true">&ldquo;</span>
          <p
            className={classNames(
              'flashcard-item__quoteBody',
              isMutedQuote && 'flashcard-item__quoteBody--muted',
            )}
          >
            {text}
            <span className="flashcard-item__quoteMark" aria-hidden="true"> &rdquo;</span>
          </p>
        </div>
        {displayCourseLabel && (
          <div className="flashcard-item__courseLabel flashcard-item__courseLabel--quote">
            {displayCourseLabel}
          </div>
        )}
      </div>
      {!hasBack && dismissButton(markDone)}
    </div>
  );

  return (
    <Card
      className={classNames(
        'flashcard-item',
        hasBack && 'flashcard-item--flippable',
        isDismissing && 'flashcard-item--dismissing',
      )}
      variant="light"
    >
      {hasBack ? (
        <div
          className={classNames(
            'flashcard-item__flipper',
            isFlipped && 'flashcard-item__flipper--flipped',
          )}
          role="button"
          tabIndex={0}
          aria-pressed={isFlipped}
          aria-label={intl.formatMessage(messages.flashcardFlip)}
          onClick={toggleFlip}
          onKeyDown={handleKeyDown}
        >
          {frontFace}
          <div className="flashcard-item__face flashcard-item__face--back">
            <div className="flashcard-item__frame flashcard-item__frame--back">
              {back.title && (
                <div className="flashcard-item__backTitle">{back.title}</div>
              )}
              {back.body && (
                <p className="flashcard-item__backBody">{back.body}</p>
              )}
              {back.footnote && (
                <div className="flashcard-item__backFootnote">{back.footnote}</div>
              )}
            </div>
          </div>
        </div>
      ) : frontFace}
    </Card>
  );
};

TextFlashcard.propTypes = {
  text: PropTypes.string.isRequired,
  courseLabel: PropTypes.string,
  quoteTone: PropTypes.oneOf(['default', 'muted']),
  back: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.string,
    footnote: PropTypes.string,
  }),
  /** (rating, holdMs) — fired once, on the first flip (or on the check button
   * for a back-less card, which has nothing to flip). */
  onAttempt: PropTypes.func,
  isDismissing: PropTypes.bool,
};

export default TextFlashcard;
